import { Link } from 'react-router-dom'
import {
  Satellite,
  Zap,
  Globe,
  Signal,
  Wifi,
  Clock,
  CheckCircle2,
  ArrowRight,
  Radio,
  Layers,
  MapPin,
} from 'lucide-react'
import SiluxPromise from '@/components/SiluxPromise'
import SEOHead from '@/components/SEOHead'

export default function StarlinkBroadband() {
  return (
    <div className="max-w-6xl mx-auto py-12 px-4 space-y-16">
      <SEOHead
        title="Starlink Satellite Broadband UK | Silux Mobile"
        description="High-speed Starlink satellite broadband through Silux Mobile — fast, reliable internet anywhere in the UK, ideal for rural homes, caravans, farms and remote sites with no fixed-line coverage."
        canonical="https://www.siluxmobile.co.uk/broadband/starlink"
      />

      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white px-4 sm:px-8 py-12 sm:py-20 md:px-16 text-center">
        {/* Star-field effect */}
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(ellipse_at_20%_50%,#3b82f6,transparent_55%),radial-gradient(ellipse_at_80%_30%,#6366f1,transparent_55%),radial-gradient(ellipse_at_50%_80%,#0ea5e9,transparent_50%)]" />
        <div className="relative space-y-5">
          {/* Coming Soon badge */}
          <div className="inline-flex items-center gap-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-4 py-1.5 text-sm font-semibold text-blue-300 uppercase tracking-widest">
            <Satellite className="w-4 h-4" />
            Coming Soon
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Starlink Satellite<br className="hidden md:block" />
            <span className="text-blue-400"> Broadband</span>
          </h1>
          <p className="text-lg md:text-xl text-white/75 max-w-3xl mx-auto leading-relaxed">
            High-speed internet from space — anywhere in the UK. Powered by SpaceX's
            constellation of over 7,000 low-orbit satellites, Starlink delivers
            fibre-like speeds to homes, businesses, and remote locations nationwide.
            Silux Mobile is bringing this fast broadband provider and rural broadband provider service to UK customers — an alternative broadband provider for anywhere fixed lines can't reach.
          </p>
          <div className="flex justify-center gap-4 flex-wrap pt-2">
            <Link
              to="/contact?subject=Starlink+Satellite+Broadband+Interest"
              className="px-7 py-3 bg-blue-500 hover:bg-blue-400 text-white rounded-xl font-semibold transition-colors"
            >
              Register Your Interest
            </Link>
            <a
              href="#how-it-works"
              className="px-7 py-3 bg-white/10 border border-white/20 text-white rounded-xl font-semibold hover:bg-white/20 transition-colors"
            >
              How It Works
            </a>
          </div>
        </div>

        {/* Satellite orbit illustration */}
        <div className="absolute top-6 right-6 opacity-10 hidden lg:block">
          <Satellite className="w-32 h-32 text-blue-300" />
        </div>
      </div>

      {/* Speed Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Zap, label: 'Download Speed', value: 'Up to 300 Mbps', sub: 'Typical 150–300 Mbps', color: 'text-yellow-500' },
          { icon: Signal, label: 'Upload Speed', value: 'Up to 40 Mbps', sub: 'Typical 10–40 Mbps', color: 'text-green-500' },
          { icon: Clock, label: 'Latency', value: '25–50 ms', sub: 'As low as 12 ms', color: 'text-blue-500' },
          { icon: Globe, label: 'Coverage', value: '110+ Countries', sub: 'Including all of UK', color: 'text-purple-500' },
        ].map(({ icon: Icon, label, value, sub, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-center space-y-2">
            <Icon className={`w-8 h-8 mx-auto ${color}`} />
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</p>
            <p className="text-xl font-bold text-gray-900">{value}</p>
            <p className="text-xs text-gray-400">{sub}</p>
          </div>
        ))}
      </div>

      <SiluxPromise variant="broadband" />

      {/* How It Works */}
      <div id="how-it-works" className="space-y-8">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold text-gray-900">How Starlink Works</h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Unlike traditional satellite internet that uses a single geostationary satellite
            35,000 km away, Starlink uses thousands of satellites orbiting just 550 km above
            Earth — dramatically reducing latency and improving speeds.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Satellite,
              step: '01',
              title: 'Low Earth Orbit Satellites',
              desc: 'Over 7,355 satellites orbit 550 km above Earth — 65× closer than traditional geostationary satellites. This proximity means signals travel a much shorter distance, reducing latency from 600 ms down to just 25–50 ms.',
              color: 'bg-blue-50 text-blue-600 border-blue-100',
            },
            {
              icon: Radio,
              step: '02',
              title: 'Your Starlink Dish',
              desc: 'A compact phased-array dish (no moving parts) automatically tracks up to 100 satellites per hour. It connects to the nearest overhead satellite and hands off seamlessly as satellites move across the sky.',
              color: 'bg-purple-50 text-purple-600 border-purple-100',
            },
            {
              icon: Wifi,
              step: '03',
              title: 'Instant Wi-Fi at Home',
              desc: 'The included Wi-Fi 6 router delivers a strong signal throughout your home. Setup takes under 30 minutes — no engineer visit, no digging up your garden, no waiting weeks for installation.',
              color: 'bg-green-50 text-green-600 border-green-100',
            },
          ].map(({ icon: Icon, step, title, desc, color }) => (
            <div key={step} className={`rounded-2xl border p-6 space-y-3 ${color}`}>
              <div className="flex items-center gap-3">
                <span className="text-3xl font-black opacity-30">{step}</span>
                <Icon className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        {/* Satellite vs Traditional comparison */}
        <div className="bg-gradient-to-r from-slate-900 to-blue-950 rounded-2xl p-6 md:p-8 text-white">
          <h3 className="text-xl font-bold mb-6 text-center">Starlink vs Traditional Satellite</h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="space-y-1">
              <p className="text-sm text-white/50 uppercase tracking-wide">Feature</p>
              <p className="text-white/30 text-sm mt-3">Orbital altitude</p>
              <p className="text-white/30 text-sm mt-3">Latency</p>
              <p className="text-white/30 text-sm mt-3">Download speeds</p>
              <p className="text-white/30 text-sm mt-3">Number of satellites</p>
            </div>
            <div className="space-y-1 bg-blue-500/10 rounded-xl p-4 border border-blue-400/20">
              <p className="text-sm text-blue-300 uppercase tracking-wide font-semibold">Starlink (LEO)</p>
              <p className="text-white text-sm mt-3 font-medium">~550 km</p>
              <p className="text-green-400 text-sm mt-3 font-medium">25–50 ms</p>
              <p className="text-green-400 text-sm mt-3 font-medium">150–300 Mbps</p>
              <p className="text-white text-sm mt-3 font-medium">7,355+</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-white/50 uppercase tracking-wide">Traditional Satellite</p>
              <p className="text-white/60 text-sm mt-3">~35,786 km</p>
              <p className="text-red-400 text-sm mt-3">600–700 ms</p>
              <p className="text-red-400 text-sm mt-3">10–50 Mbps</p>
              <p className="text-white/60 text-sm mt-3">1–3</p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Benefits */}
      <div className="space-y-8">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold text-gray-900">Why Choose Starlink?</h2>
          <p className="text-gray-500 max-w-xl mx-auto">
            Ideal for homes and businesses that can't get reliable fixed-line broadband — a cheap internet provider UK option and rural broadband provider for hard-to-reach areas.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            {
              icon: MapPin,
              title: 'Reach Anywhere',
              desc: 'Perfect for rural properties, farms, remote businesses, boats, and any location where cable broadband simply doesn\'t reach.',
            },
            {
              icon: Zap,
              title: 'Fibre-Like Speeds',
              desc: 'Download speeds of 150–300 Mbps are comparable to standard fibre broadband. Stream, video call, and work from home without compromise.',
            },
            {
              icon: Clock,
              title: 'Ultra-Low Latency',
              desc: 'At 25–50 ms, Starlink latency is drastically lower than traditional satellite. Online gaming, video calls, and real-time applications all work smoothly.',
            },
            {
              icon: Layers,
              title: 'Quick Self-Installation',
              desc: 'No engineer needed. The Starlink kit arrives ready to set up. Point the dish at the sky, plug in the router, and you\'re online in under 30 minutes.',
            },
            {
              icon: Satellite,
              title: 'Unlimited Data',
              desc: 'Residential plans include unlimited data — stream 4K, back up to the cloud and browse with no speed throttling (fair use policy applies).',
            },
            {
              icon: Globe,
              title: 'Global Coverage',
              desc: 'Available across the UK and 110+ countries. Portability plans let you take your connection on the road, to your caravan, or abroad.',
            },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex gap-4 p-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <Icon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hardware */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl p-6 md:p-10 space-y-8">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold text-gray-900">What's in the Kit</h2>
          <p className="text-gray-500 max-w-xl mx-auto">
            Everything you need arrives in one box — dish, mount, and Wi-Fi 6 router included.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
            <div className="flex items-center gap-3">
              <Satellite className="w-8 h-8 text-blue-600" />
              <h3 className="text-lg font-bold text-gray-900">Starlink Dish (Gen 3)</h3>
            </div>
            <ul className="space-y-2 text-sm text-gray-600">
              {[
                '23.4" × 15.07" phased-array antenna',
                'No moving parts — electronically steered',
                'Automatically tracks satellites as they pass',
                'Weather-resistant with built-in snow-melt',
                'IP69K waterproof rating',
                'Simple pole or roof mount included',
              ].map(f => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
            <div className="flex items-center gap-3">
              <Wifi className="w-8 h-8 text-purple-600" />
              <h3 className="text-lg font-bold text-gray-900">Wi-Fi 6 Router (Gen 3)</h3>
            </div>
            <ul className="space-y-2 text-sm text-gray-600">
              {[
                'Wi-Fi 6 (802.11ax) — faster & more efficient',
                'Tri-band 4×4 MU-MIMO technology',
                'Covers large homes and offices',
                'Ethernet ports for wired devices',
                'Compact 5.4" × 3.3" × 1" form factor',
                'Supports mesh expansion nodes',
              ].map(f => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Ideal For */}
      <div className="space-y-6">
        <h2 className="text-3xl font-bold text-gray-900 text-center">Who Is It For?</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { emoji: '🏡', title: 'Rural Homes', desc: 'Finally get fast broadband where fibre cables will never reach.' },
            { emoji: '🌾', title: 'Farms & Agriculture', desc: 'Keep IoT sensors, CCTV, and farm management systems online 24/7.' },
            { emoji: '🏢', title: 'Remote Businesses', desc: 'Reliable internet for offices and sites outside cable coverage areas.' },
            { emoji: '⛵', title: 'Boats & Caravans', desc: 'Take high-speed internet with you anywhere in the UK and beyond.' },
          ].map(({ emoji, title, desc }) => (
            <div key={title} className="text-center p-6 bg-white rounded-2xl border border-gray-100 shadow-sm space-y-2">
              <div className="text-4xl">{emoji}</div>
              <h3 className="font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-500">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Coming Soon CTA */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white text-center px-6 py-14 space-y-5">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_50%,#bfdbfe,transparent_60%)]" />
        <div className="relative space-y-5">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-semibold uppercase tracking-widest">
            <Satellite className="w-4 h-4" />
            Coming Soon to Silux Mobile
          </div>
          <h2 className="text-3xl md:text-4xl font-bold">Be First to Know When We Launch</h2>
          <p className="text-white/75 max-w-xl mx-auto text-lg">
            We're working with Starlink to bring satellite broadband to our customers.
            Register your interest today and we'll contact you the moment it's available.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link
              to="/contact?subject=Starlink+Satellite+Broadband+Interest"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-blue-700 rounded-xl font-bold hover:bg-blue-50 transition-colors"
            >
              Register Your Interest
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-white/50 text-sm">No commitment required — we'll notify you when it's ready.</p>
        </div>
      </div>

    </div>
  )
}
